import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PiCalendarDuotone, PiCaretLeftBold, PiCaretRightBold, PiClockDuotone } from 'react-icons/pi';

/* ── Константы ──────────────────────────────────────────── */

const WEEKDAY_LABELS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const MONTH_LABELS = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5);

/* ── Утилиты ────────────────────────────────────────────── */

function pad(n) {
  return String(n).padStart(2, '0');
}

function fromDatetimeLocal(value) {
  if (!value) return null;
  const [date, time] = value.split('T');
  if (!date || !time) return null;
  const [h, m] = time.split(':').map(Number);
  const [y, mo, d] = date.split('-').map(Number);
  return { year: y, month: mo, day: d, hour: h ?? 0, minute: m ?? 0 };
}

function toDatetimeLocal(parts) {
  if (!parts) return '';
  const { year, month, day, hour, minute } = parts;
  return `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}`;
}

function daysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

function firstDayOffset(year, month) {
  const day = new Date(year, month - 1, 1).getDay();
  return day === 0 ? 6 : day - 1; // Monday = 0
}

function calendarGrid(year, month) {
  const total = daysInMonth(year, month);
  const offset = firstDayOffset(year, month);
  const cells = [];
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= total; d++) cells.push(d);
  return cells;
}

function isToday(y, m, d) {
  const now = new Date();
  return now.getFullYear() === y && now.getMonth() + 1 === m && now.getDate() === d;
}

/* ── Компонент ──────────────────────────────────────────── */

export default function DateTimePicker({
  label,
  value,          // "YYYY-MM-DDTHH:mm" (datetime-local string)
  onChange,       // (newValue: string) => void
  disabled = false,
  error,
  min,            // optional datetime-local min
  max,            // optional datetime-local max
  showTime = true,
}) {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('date'); // 'date' | 'time'

  const selected = useMemo(() => fromDatetimeLocal(value), [value]);

  const [viewYear, setViewYear] = useState(() => selected?.year ?? new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(() => selected?.month ?? new Date().getMonth() + 1);

  // синхронизируем view при внешнем изменении value
  useEffect(() => {
    if (selected) {
      setViewYear(selected.year);
      setViewMonth(selected.month);
    }
  }, [selected]);

  // закрытие по клику вне
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const grid = useMemo(() => calendarGrid(viewYear, viewMonth), [viewYear, viewMonth]);

  const prevMonth = useCallback(() => {
    setViewMonth((m) => {
      if (m === 1) { setViewYear((y) => y - 1); return 12; }
      return m - 1;
    });
  }, []);

  const nextMonth = useCallback(() => {
    setViewMonth((m) => {
      if (m === 12) { setViewYear((y) => y + 1); return 1; }
      return m + 1;
    });
  }, []);

  const pickDate = useCallback((day) => {
    const base = selected ?? { hour: 0, minute: 0 };
    const next = { ...base, year: viewYear, month: viewMonth, day };
    onChange(toDatetimeLocal(next));
    if (showTime) setTab('time');
    else setOpen(false);
  }, [selected, viewYear, viewMonth, onChange, showTime]);

  const pickTime = useCallback((hour, minute) => {
    const base = selected ?? { year: viewYear, month: viewMonth, day: new Date().getDate() };
    onChange(toDatetimeLocal({ ...base, hour, minute }));
  }, [selected, viewYear, viewMonth, onChange]);

  const displayValue = useMemo(() => {
    if (!selected) return '';
    const { year, month, day, hour, minute } = selected;
    const dateStr = `${pad(day)}.${pad(month)}.${year}`;
    return showTime ? `${dateStr}, ${pad(hour)}:${pad(minute)}` : dateStr;
  }, [selected, showTime]);

  const isDayDisabled = useCallback((day) => {
    if (!day) return true;
    const ts = new Date(viewYear, viewMonth - 1, day).getTime();
    if (min) { const minD = new Date(min.split('T')[0]); if (ts < minD.getTime()) return true; }
    if (max) { const maxD = new Date(max.split('T')[0]); if (ts > maxD.getTime()) return true; }
    return false;
  }, [viewYear, viewMonth, min, max]);

  return (
    <div className={`bm-dtp${disabled ? ' bm-dtp--disabled' : ''}${error ? ' bm-dtp--error' : ''}`} ref={rootRef}>
      {label && <label className="bm-dtp__label">{label}</label>}

      <button
        type="button"
        className="bm-dtp__trigger"
        disabled={disabled}
        onClick={() => { setOpen((o) => !o); setTab('date'); }}
      >
        <PiCalendarDuotone size={16} className="bm-dtp__trigger-icon" />
        <span className={`bm-dtp__trigger-text${!displayValue ? ' bm-dtp__trigger-text--placeholder' : ''}`}>
          {displayValue || 'Выберите дату'}
        </span>
      </button>

      {error && <span className="bm-input-error-msg">{error}</span>}

      {open && !disabled && (
        <div className="bm-dtp__dropdown">
          {/* Табы */}
          {showTime && (
            <div className="bm-dtp__tabs">
              <button
                type="button"
                className={`bm-dtp__tab${tab === 'date' ? ' bm-dtp__tab--active' : ''}`}
                onClick={() => setTab('date')}
              >
                <PiCalendarDuotone size={14} /> Дата
              </button>
              <button
                type="button"
                className={`bm-dtp__tab${tab === 'time' ? ' bm-dtp__tab--active' : ''}`}
                onClick={() => setTab('time')}
              >
                <PiClockDuotone size={14} /> Время
              </button>
            </div>
          )}

          {/* Календарь */}
          {tab === 'date' && (
            <div className="bm-dtp__calendar">
              <div className="bm-dtp__cal-header">
                <button type="button" className="bm-dtp__cal-nav" onClick={prevMonth}>
                  <PiCaretLeftBold size={14} />
                </button>
                <span className="bm-dtp__cal-title">
                  {MONTH_LABELS[viewMonth - 1]} {viewYear}
                </span>
                <button type="button" className="bm-dtp__cal-nav" onClick={nextMonth}>
                  <PiCaretRightBold size={14} />
                </button>
              </div>

              <div className="bm-dtp__cal-weekdays">
                {WEEKDAY_LABELS.map((d) => (
                  <span key={d} className="bm-dtp__cal-wd">{d}</span>
                ))}
              </div>

              <div className="bm-dtp__cal-grid">
                {grid.map((day, i) => {
                  const sel = day && selected && selected.year === viewYear && selected.month === viewMonth && selected.day === day;
                  const today = day && isToday(viewYear, viewMonth, day);
                  const dis = isDayDisabled(day);
                  return (
                    <button
                      key={i}
                      type="button"
                      disabled={dis}
                      className={[
                        'bm-dtp__cal-cell',
                        sel && 'bm-dtp__cal-cell--selected',
                        today && 'bm-dtp__cal-cell--today',
                        !day && 'bm-dtp__cal-cell--empty',
                      ].filter(Boolean).join(' ')}
                      onClick={() => day && pickDate(day)}
                    >
                      {day || ''}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Время */}
          {tab === 'time' && (
            <div className="bm-dtp__time">
              <div className="bm-dtp__time-col">
                <span className="bm-dtp__time-heading">Час</span>
                <div className="bm-dtp__time-scroll">
                  {HOURS.map((h) => (
                    <button
                      key={h}
                      type="button"
                      className={`bm-dtp__time-cell${selected?.hour === h ? ' bm-dtp__time-cell--active' : ''}`}
                      onClick={() => pickTime(h, selected?.minute ?? 0)}
                    >
                      {pad(h)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="bm-dtp__time-col">
                <span className="bm-dtp__time-heading">Мин</span>
                <div className="bm-dtp__time-scroll">
                  {MINUTES.map((m) => (
                    <button
                      key={m}
                      type="button"
                      className={`bm-dtp__time-cell${selected?.minute === m ? ' bm-dtp__time-cell--active' : ''}`}
                      onClick={() => pickTime(selected?.hour ?? 0, m)}
                    >
                      {pad(m)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Подтвердить */}
          {showTime && (
            <div className="bm-dtp__footer">
              <button
                type="button"
                className="bm-dtp__confirm"
                onClick={() => setOpen(false)}
              >
                Готово
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
